// apps/web/src/features/bookings/components/time-selection/EndTimeCard.tsx
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import Chip from '@mui/material/Chip';
import { styled } from '@mui/material/styles';
import type { EndTimeOption } from '@lov/shared';

interface EndTimeCardProps {
  option: EndTimeOption;
  isSelected: boolean;
  onSelect: () => void;
  currency?: string;
  bookingMode?: 'capacity' | 'inventory';
}

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})<{ isSelected: boolean }>(({ theme, isSelected }) => ({
  borderRadius: 12,
  transition: 'all 0.2s ease',
  border: isSelected
    ? `2px solid ${theme.palette.primary.main}`
    : `1px solid ${theme.palette.divider}`,
  boxShadow: isSelected ? theme.shadows[4] : theme.shadows[1],
  '&:hover': {
    borderColor: theme.palette.primary.main,
    boxShadow: theme.shadows[3],
  },
}));

function formatTime(time: string): string {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minutes} ${ampm}`;
}

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function EndTimeCard({
  option,
  isSelected,
  onSelect,
  currency = 'INR',
  bookingMode,
}: EndTimeCardProps) {
  return (
    <StyledCard isSelected={isSelected}>
      <CardActionArea onClick={onSelect} sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {option.durationLabel}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ends at {formatTime(option.endTime)}
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            {bookingMode !== 'inventory' && (
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                {formatCurrency(option.price, currency)}
              </Typography>
            )}
            {option.isRecommended && (
              <Chip
                label="Popular"
                size="small"
                color="primary"
                variant="outlined"
                sx={{ mt: 0.5 }}
              />
            )}
          </Box>
        </Box>
      </CardActionArea>
    </StyledCard>
  );
}
